
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/auth/AuthContext";
import ShoppingHeader from "@/components/shopping/ShoppingHeader";
import ShoppingContent from "@/components/shopping/ShoppingContent";
import AddItemButton from "@/components/shopping/AddItemButton";
import AddItemDialog from "@/components/shopping/AddItemDialog";
import { ShoppingItemWithProduct, ShoppingList } from "@/types";
import { useShoppingPageData } from "@/hooks/shopping/useShoppingPageData";
import { useShoppingListActions } from "@/hooks/shopping/useShoppingListActions";
import { useThemeConfig } from "@/components/ui/theme-config-provider";
import NetworkStatus from '@/components/layout/NetworkStatus';
import { useTutorialManager } from "@/hooks/useTutorialManager";
import TutorialSpotlight from "@/components/tutorial/TutorialSpotlight";
import { shoppingPageTutorial } from "@/components/shopping/ShoppingPageTutorial";
import { useResetModals } from '@/hooks/useResetModals';
import { useShoppingItemsSubscription } from '@/hooks/useShoppingItemsSubscription';
import { useModeAnnouncement } from "@/hooks/useModeAnnouncement";
import { exportShoppingList, ShareFallbackPayload } from "@/services/shopping/exportShoppingList.service";
import ShareFallbackSheet from "@/components/shopping/ShareFallbackSheet";

const ShoppingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const listId = new URLSearchParams(location.search).get('list');
  const modeParam = new URLSearchParams(location.search).get('mode');
  const { user } = useAuth();
  useResetModals();
  useShoppingItemsSubscription(listId);
  const { theme } = useThemeConfig();
  const queryClient = useQueryClient();
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const initialMode = modeParam === 'prepare' ? 'preparazione' : 'spesa';
  const [mode, setMode] = useState<'spesa' | 'preparazione'>(initialMode);
  const [filter, setFilter] = useState<string>(initialMode === 'preparazione' ? 'all' : 'active');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  useEffect(() => {
    console.debug('[ShoppingPage] selectedCategories', selectedCategories);
    // Include IDs from "missing categories" logs to verify filter behavior when needed
  }, [selectedCategories]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tutorialTarget, setTutorialTarget] = useState<string | null>(null);
  const { announceMode } = useModeAnnouncement();
  const [shareableItems, setShareableItems] = useState<ShoppingItemWithProduct[]>([]);
  const lastShareableSignatureRef = useRef<string | null>(null);
  const [shareFallbackData, setShareFallbackData] = useState<ShareFallbackPayload | null>(null);
  const [isShareFallbackOpen, setIsShareFallbackOpen] = useState(false);

  const handleShareableItemsChange = useCallback((items: ShoppingItemWithProduct[]) => {
    const signature = `${items.length}:${items
      .map((item) => `${item.id}:${item.checked ? 1 : 0}:${item.updated_at ?? ""}`)
      .join("|")}`;

    if (lastShareableSignatureRef.current === signature) {
      return;
    }

    lastShareableSignatureRef.current = signature;
    setShareableItems(items);
  }, []);

  const {
    list,
    isLoading,
    hasMultipleLists
  } = useShoppingPageData({ listId, navigate });

  const typedList = list as ShoppingList | undefined;

  const handleExportList = useCallback(async () => {
    if (!typedList) {
      return;
    }

    const result = await exportShoppingList({
      listName: typedList.name,
      items: shareableItems,
    });
    if (result.status === 'fallback') {
      setShareFallbackData(result.payload);
      setIsShareFallbackOpen(true);
      return;
    }

    setShareFallbackData(null);
    setIsShareFallbackOpen(false);
  }, [typedList, shareableItems]);

  const handleShareFallbackOpenChange = useCallback((open: boolean) => {
    setIsShareFallbackOpen(open);
    if (!open) {
      setShareFallbackData(null);
    }
  }, []);

  // Tutorial integration
  const { isOpen: isTutorialOpen, closeTutorial, isAnyTutorialOpen, closeAnyTutorial } = useTutorialManager({
    pageId: 'shopping-page',
    showOnFirstVisit: true
  });

  useEffect(() => {
    if (isAnyTutorialOpen) {
      closeAnyTutorial();
    }
    // The tutorial will open automatically inside useTutorialManager
  }, []);

  const { handleRapidInsertChange, handleItemAddSuccess, handleShopSelect } = useShoppingListActions({
    queryClient,
    listId
  });

  const handleShopSelectLocal = async (shopId: string | null) => {
    setActiveShopId(shopId);
    try {
      await handleShopSelect(shopId);
    } catch (err) {
      console.error('Errore in handleShopSelectLocal', err);
    }
    if (listId) {
      await queryClient.invalidateQueries({ queryKey: ['shopping-list', listId] });
    }
  };

  const handleModeChange = (newMode: 'spesa' | 'preparazione') => {
    setMode(newMode);
    if (newMode === 'preparazione') {
      setFilter('all');
      announceMode('Modalità Prepara');
    } else {
      setFilter('active');
      announceMode('Modalità Spesa');
    }
  };
  
  const [activeShopId, setActiveShopId] = useState<string | null>(null);

  // Quando arriva il dato da Supabase, aggiorna lo stato
  useEffect(() => {
    if (typedList && typedList.shop_id !== undefined) {
      setActiveShopId(typedList.shop_id ?? null);
    }
  }, [typedList]);

  if (isLoading) {
    return (
      <div className="min-viewport-height flex justify-center items-center" style={{
        backgroundColor: theme.backgroundColor || '#f1f0fb',
        color: theme.textColor || '#000000'
      }}>
        <p style={{ color: theme.sectionTextColor }}>Caricamento lista...</p>
      </div>
    );
  }

  if (!typedList || !user) {
    return (
      <div className="min-viewport-height flex justify-center items-center" style={{
        backgroundColor: theme.backgroundColor || '#f1f0fb',
        color: theme.textColor || '#000000'
      }}>
        <div className="text-center p-4">
          <h2 className="font-bold text-xl mb-2">Lista non trovata</h2>
          <p className="mb-4" style={{ color: theme.sectionTextColor }}>
            La lista che stai cercando non esiste o non hai accesso.
          </p>
          <button
            onClick={() => navigate('/lists')}
            className="px-4 py-2 font-medium transition-colors"
            style={{
              backgroundColor: theme.primaryColor || '#4caf50',
              borderRadius: theme.buttonBorderRadius,
              padding: theme.buttonPadding,
              color: theme.textPrimaryColor,
              fontWeight: 'bold',
            }}
          >
            Torna alle liste
          </button>
        </div>
      </div>
    );
  }
  
  // The custom background color is displayed as a 10px strip on the left.
  const barBackground = typedList.backgroundGradient;
  
  return (
    <div
      className="min-viewport-height relative"
      style={{
        backgroundColor: theme.backgroundColor || '#f1f0fb',
        color: theme.textColor || '#000000'
      }}
    >
      {barBackground && (
        <div
          className="absolute left-0 top-0 h-full w-[12px] z-10"
          style={{ background: barBackground }}
        />
      )}
      <ShoppingHeader
        listId={typedList.id}
        rapidInsert={typedList.rapid_insert}
        onRapidInsertChange={handleRapidInsertChange}
        listName={typedList.name}
        searchOpen={searchOpen}
        searchTerm={searchTerm}
        filter={filter}
        selectedCategories={selectedCategories}
        setSearchOpen={setSearchOpen}
        setSearchTerm={setSearchTerm}
        setFilter={setFilter}
        setSelectedCategories={setSelectedCategories}
        isPrimary={typedList.isPrimary || false}
        hasMultipleLists={hasMultipleLists}
        shopId={activeShopId}
        onShopSelect={handleShopSelectLocal}
        onTutorialStepChange={setTutorialTarget}
        mode={mode}
        onModeChange={handleModeChange}
        isImportDialogOpen={isImportDialogOpen}
        onImportDialogOpenChange={setIsImportDialogOpen}
        onExportList={() => { void handleExportList(); }}
        canExport={shareableItems.length > 0}
      />


      <ShoppingContent
        list={typedList}
        filter={filter}
        selectedCategories={selectedCategories}
        searchTerm={searchTerm}
        backgroundColor={theme.backgroundColor}
        searchOpen={searchOpen}
        activeShopId={activeShopId}
        onShareableItemsChange={handleShareableItemsChange}
      />

      {!isImportDialogOpen && (
        <AddItemButton
          onClick={() => setIsAddingItem(true)}
          dimmed={tutorialTarget !== null && tutorialTarget !== '.tutorial-target-shoppingheader-additem'}
        />
      )}
      
      <NetworkStatus />
      
      <AddItemDialog
        listId={typedList.id}
        isOpen={isAddingItem}
        onOpenChange={setIsAddingItem}
        rapidInsert={typedList.rapid_insert}
      />

      <TutorialSpotlight
        steps={shoppingPageTutorial}
        isOpen={isTutorialOpen}
        onClose={closeTutorial}
        pageId="shopping-page"
        highlightColor={theme.primaryColor}
      />

      {shareFallbackData && (
        <ShareFallbackSheet
          open={isShareFallbackOpen}
          onOpenChange={handleShareFallbackOpenChange}
          shareTitle={shareFallbackData.title}
          shareText={shareFallbackData.text}
        />
      )}
    </div>
  );
};

export default ShoppingPage;
